import React, { useState } from 'react';
import { X, Copy } from 'lucide-react';
import { Location } from '@/firebase/locationService';

interface User {
  id: string;
  name: string;
  email: string;
  locationIds?: string[];
}

interface AssignLocationsPanelProps {
  user: User;
  locations: Location[];
  users: User[]; // All users for "Copy from User"
  onAssign: (locationIds: string[]) => Promise<void>;
  onClose: () => void;
}

const AssignLocationsPanel: React.FC<AssignLocationsPanelProps> = ({ 
  user, 
  locations, 
  users,
  onAssign, 
  onClose 
}) => {
  const [assignedLocationIds, setAssignedLocationIds] = useState<string[]>(user.locationIds || []);
  const [selectedState, setSelectedState] = useState<string>('All States');

  const toggleLocation = (locationId: string) => {
    setAssignedLocationIds(prev => {
      const isSelected = prev.includes(locationId);
      if (isSelected) {
        return prev.filter(id => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allLocationIds = locations.map(l => l.id).filter(Boolean) as string[];
      setAssignedLocationIds(allLocationIds);
    } else {
      setAssignedLocationIds([]);
    }
  };

  // Get unique states for filtering
  const states = Array.from(new Set(locations.map(l => l.state))).sort();
  
  // Filter locations by state
  const filteredLocations = locations.filter(location => {
    return selectedState === 'All States' || location.state === selectedState;
  });

  const areAllSelected = filteredLocations.length > 0 &&
    filteredLocations.every(l => assignedLocationIds.includes(l.id!));

  // Get other users (excluding current)
  const otherUsers = users.filter(u => u.id !== user.id);

  const handleCopyFromUser = (sourceUserId: string) => {
    const sourceUser = users.find(u => u.id === sourceUserId);
    if (sourceUser && sourceUser.locationIds) {
      // Only include location IDs that exist in the current organization's locations
      const validLocationIds = sourceUser.locationIds.filter(locationId => 
        locations.some(loc => loc.id === locationId)
      );
      setAssignedLocationIds(validLocationIds);
    }
  };

  const handleSave = async () => {
    await onAssign(assignedLocationIds);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-[120]">
      <div className="bg-white w-full max-w-2xl h-full flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Assign Locations</h2>
              <p className="text-sm text-gray-600 mt-1">{user.name} ({user.email})</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Copy from User */}
          {otherUsers.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <Copy className="w-4 h-4 text-gray-600" />
                <label className="text-sm font-medium text-gray-700">Copy from User:</label>
                <select
                  onChange={(e) => e.target.value && handleCopyFromUser(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  defaultValue=""
                >
                  <option value="">Select a user to copy...</option>
                  {otherUsers.map(usr => {
                    // Count only locations that exist in current organization
                    const validLocationCount = usr.locationIds?.filter(locationId => 
                      locations.some(loc => loc.id === locationId)
                    ).length || 0;
                    
                    return (
                      <option key={usr.id} value={usr.id}>
                        {usr.name} ({validLocationCount} locations)
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}

          {/* State Filter */}
          <div>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="All States">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Location List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredLocations.length === 0 ? (
            <p className="text-sm text-gray-500">No locations available in selected state.</p>
          ) : (
            <>
              {/* Select All Checkbox */}
              <div className="pb-3 mb-3 border-b border-gray-200">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={areAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-900">
                    Select All ({filteredLocations.length} locations)
                  </span>
                </label>
              </div>

              <div className="space-y-2">
                {filteredLocations.map(location => (
                  <label
                    key={location.id}
                    className="flex items-start space-x-2 cursor-pointer hover:bg-gray-50 p-3 rounded border border-gray-200"
                    title={`${location.name}

Address: ${location.address}
City: ${location.city || 'N/A'}
State: ${location.state}
Country: ${location.country}`}
                  >
                    <input
                      type="checkbox"
                      checked={assignedLocationIds.includes(location.id!)}
                      onChange={() => location.id && toggleLocation(location.id)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm text-gray-900 font-medium">
                        {location.name}
                      </span>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs text-gray-500">{location.state}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500">{location.country}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {assignedLocationIds.length} location(s) selected
          </p>
          <div className="flex space-x-3">
            <button onClick={onClose} className="btn-secondary px-5 py-2">
              Cancel
            </button>
            <button onClick={handleSave} className="btn-primary px-5 py-2">
              Save Assignment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignLocationsPanel;

